import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { userService } from '../services/userService';
import { log } from '../lib/logger';

interface InfluencerProtectedRouteProps {
  children: React.ReactNode;
}

export function InfluencerProtectedRoute({ children }: InfluencerProtectedRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const [isInfluencer, setIsInfluencer] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewAsParam = searchParams.get('as');

  useEffect(() => {
    const checkInfluencerAccess = async () => {
      if (authLoading) {
        return;
      }

      if (!user) {
        // Not authenticated - redirect to influencer login
        navigate('/creatorshub', { replace: true });
        return;
      }

      try {
        // Check if user has influencer access
        const { data: authData, error } = await supabase
          .from('influencer_auth')
          .select('id, status')
          .eq('auth_user_id', user.id)
          .maybeSingle();

        if (error) {
          log.error('Error checking influencer access:', error);
          navigate('/creatorshub', { replace: true });
          return;
        }

        if (!authData) {
          // Not an influencer — staff may still enter in view-as mode.
          if (viewAsParam) {
            const userRole = await userService.getCurrentUserRole();
            if (userRole && (userRole.role === 'admin' || userRole.role === 'agent')) {
              setIsInfluencer(true);
              setLoading(false);
              return;
            }
          }
          log.debug('[InfluencerProtectedRoute] User is not an influencer, redirecting to main app');
          navigate('/', { replace: true });
          return;
        }

        if (authData.status === 'suspended') {
          // Account is suspended
          await supabase.auth.signOut();
          navigate('/creatorshub', { replace: true });
          return;
        }

        // User has valid influencer access
        setIsInfluencer(true);
        setLoading(false);
      } catch (error) {
        log.error('Error in influencer access check:', error);
        navigate('/creatorshub', { replace: true });
      }
    };

    checkInfluencerAccess();
  }, [user, authLoading, navigate, viewAsParam]);

  // Show loading state while checking
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-emerald-200 border-t-emerald-600 mb-4"></div>
          <p className="text-gray-600 font-medium">A carregar o portal...</p>
        </div>
      </div>
    );
  }

  // Only render children if user has valid influencer access
  if (!isInfluencer) {
    return null;
  }

  return <>{children}</>;
}
